/**
 * World Courier booking template auto-save: detect when origin/destination form data
 * is not an exact match for any existing template (same normalized company name + fields).
 */

import { normalizeWcTemplateCountry } from './wcBookingTemplateCountry';

export interface WcBookingTemplateRowForAutosave {
  id: string;
  company_name: string;
  contact_name?: string | null;
  phone_number?: string | null;
  extension?: string | null;
  email?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state_province?: string | null;
  postal_code?: string | null;
  country?: string | null;
  pickup_instructions?: string | null;
  delivery_instructions?: string | null;
  wc_project?: string | null;
}

/** Form fields read when deciding to POST a template (subset of BookShipmentFormValues). */
export interface WcTemplateAutosaveFormValues {
  wc_origin_company_name?: string;
  wc_origin_contact_name?: string;
  wc_origin_phone_number?: string;
  wc_origin_extension?: string;
  wc_origin_email?: string;
  wc_origin_address1?: string;
  wc_origin_address2?: string;
  wc_origin_city?: string;
  wc_origin_state_province?: string;
  wc_origin_postal_code?: string;
  wc_origin_country?: string;
  wc_pickup_instructions?: string;
  wc_dest_company_name?: string;
  wc_dest_contact_name?: string;
  wc_dest_phone_number?: string;
  wc_dest_extension?: string;
  wc_dest_email?: string;
  wc_dest_address1?: string;
  wc_dest_address2?: string;
  wc_dest_city?: string;
  wc_dest_state_province?: string;
  wc_dest_postal_code?: string;
  wc_dest_country?: string;
  wc_delivery_instructions?: string;
  wc_project?: string;
}

function normField(v: unknown): string {
  if (v == null) return '';
  return String(v).trim();
}

/**
 * A template blocks autosave (i.e. counts as an existing match) when its fields
 * match AND it is compatible with the form's project. Untagged templates are
 * universal, so they block autosave under any project — this avoids creating a
 * duplicate tagged copy when a user books with an untagged template. A tagged
 * template only blocks a save for its own project.
 */
function templateBlocksAutosave(
  template: WcBookingTemplateRowForAutosave,
  formProject: string
): boolean {
  const tpl = normField(template.wc_project);
  if (!tpl) return true;
  return tpl === normField(formProject);
}

export function normalizeWcTemplateCompanyName(
  s: string | null | undefined
): string {
  return normField(s).toLowerCase();
}

function originFieldsEqual(
  t: WcBookingTemplateRowForAutosave,
  v: WcTemplateAutosaveFormValues
): boolean {
  return (
    normField(t.contact_name) === normField(v.wc_origin_contact_name) &&
    normField(t.phone_number) === normField(v.wc_origin_phone_number) &&
    normField(t.extension) === normField(v.wc_origin_extension) &&
    normField(t.email) === normField(v.wc_origin_email) &&
    normField(t.address1) === normField(v.wc_origin_address1) &&
    normField(t.address2) === normField(v.wc_origin_address2) &&
    normField(t.city) === normField(v.wc_origin_city) &&
    normField(t.state_province) === normField(v.wc_origin_state_province) &&
    normField(t.postal_code) === normField(v.wc_origin_postal_code) &&
    normalizeWcTemplateCountry(t.country) ===
      normalizeWcTemplateCountry(v.wc_origin_country) &&
    normField(t.pickup_instructions) === normField(v.wc_pickup_instructions)
  );
}

function destFieldsEqual(
  t: WcBookingTemplateRowForAutosave,
  v: WcTemplateAutosaveFormValues
): boolean {
  return (
    normField(t.contact_name) === normField(v.wc_dest_contact_name) &&
    normField(t.phone_number) === normField(v.wc_dest_phone_number) &&
    normField(t.extension) === normField(v.wc_dest_extension) &&
    normField(t.email) === normField(v.wc_dest_email) &&
    normField(t.address1) === normField(v.wc_dest_address1) &&
    normField(t.address2) === normField(v.wc_dest_address2) &&
    normField(t.city) === normField(v.wc_dest_city) &&
    normField(t.state_province) === normField(v.wc_dest_state_province) &&
    normField(t.postal_code) === normField(v.wc_dest_postal_code) &&
    normalizeWcTemplateCountry(t.country) ===
      normalizeWcTemplateCountry(v.wc_dest_country) &&
    normField(t.delivery_instructions) === normField(v.wc_delivery_instructions)
  );
}

function originTemplateExactMatch(
  t: WcBookingTemplateRowForAutosave,
  v: WcTemplateAutosaveFormValues
): boolean {
  const cn = normalizeWcTemplateCompanyName(v.wc_origin_company_name);
  if (!cn) return false;
  if (normalizeWcTemplateCompanyName(t.company_name) !== cn) return false;
  if (!originFieldsEqual(t, v)) return false;
  return templateBlocksAutosave(t, normField(v.wc_project));
}

function destTemplateExactMatch(
  t: WcBookingTemplateRowForAutosave,
  v: WcTemplateAutosaveFormValues
): boolean {
  const cn = normalizeWcTemplateCompanyName(v.wc_dest_company_name);
  if (!cn) return false;
  if (normalizeWcTemplateCompanyName(t.company_name) !== cn) return false;
  if (!destFieldsEqual(t, v)) return false;
  return templateBlocksAutosave(t, normField(v.wc_project));
}

export function shouldAutoSaveOriginTemplate(
  templates: WcBookingTemplateRowForAutosave[],
  v: WcTemplateAutosaveFormValues
): boolean {
  if (!normalizeWcTemplateCompanyName(v.wc_origin_company_name)) return false;
  return !templates.some((t) => originTemplateExactMatch(t, v));
}

export function shouldAutoSaveDestTemplate(
  templates: WcBookingTemplateRowForAutosave[],
  v: WcTemplateAutosaveFormValues
): boolean {
  if (!normalizeWcTemplateCompanyName(v.wc_dest_company_name)) return false;
  return !templates.some((t) => destTemplateExactMatch(t, v));
}

export function buildWcOriginTemplatePostBody(
  v: WcTemplateAutosaveFormValues
): Record<string, unknown> {
  return {
    company_name: normField(v.wc_origin_company_name) || null,
    contact_name: normField(v.wc_origin_contact_name) || null,
    phone_number: normField(v.wc_origin_phone_number) || null,
    extension: normField(v.wc_origin_extension) || null,
    email: normField(v.wc_origin_email) || null,
    address1: normField(v.wc_origin_address1) || null,
    address2: normField(v.wc_origin_address2) || null,
    city: normField(v.wc_origin_city) || null,
    state_province: normField(v.wc_origin_state_province) || null,
    postal_code: normField(v.wc_origin_postal_code) || null,
    country: normalizeWcTemplateCountry(v.wc_origin_country) || null,
    pickup_instructions: normField(v.wc_pickup_instructions) || null,
    delivery_instructions: null,
    wc_project: normField(v.wc_project) || null,
  };
}

export function buildWcDestTemplatePostBody(
  v: WcTemplateAutosaveFormValues
): Record<string, unknown> {
  return {
    company_name: normField(v.wc_dest_company_name) || null,
    contact_name: normField(v.wc_dest_contact_name) || null,
    phone_number: normField(v.wc_dest_phone_number) || null,
    extension: normField(v.wc_dest_extension) || null,
    email: normField(v.wc_dest_email) || null,
    address1: normField(v.wc_dest_address1) || null,
    address2: normField(v.wc_dest_address2) || null,
    city: normField(v.wc_dest_city) || null,
    state_province: normField(v.wc_dest_state_province) || null,
    postal_code: normField(v.wc_dest_postal_code) || null,
    country: normalizeWcTemplateCountry(v.wc_dest_country) || null,
    pickup_instructions: null,
    delivery_instructions: normField(v.wc_delivery_instructions) || null,
    wc_project: normField(v.wc_project) || null,
  };
}
